import { FC, useEffect } from 'react'
// https://fluentsite.z22.web.core.windows.net/quick-start
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  Spinner,
  tokens
} from '@fluentui/react-components'
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom'
import { useTeamsUserCredential } from '@microsoft/teamsfx-react'
import { changeLanguage } from 'i18next'
import { app } from '@microsoft/teams-js'

import Privacy from './Privacy'
import TermsOfUse from './TermsOfUse'
import { Tab } from './Tab/Tab'
import { ConfigurableTab } from './ConfigurableTab/ConfigurableTab'
import { DisplayTab } from './ConfigurableTab/DisplayTab'
import { TeamsFxContext } from './Context'
import { AuthStart } from './Auth/AuthStart'
import { AuthCallback } from './Auth/AuthCallback'
import config from '../config'

import './App.css'

/**
 * The main app which handles the initialization and routing
 * of the app.
 */

interface ContextResponse { // An interface to describe the shape of the context object
  app: {
    host: {
      clientType: string
    }
    locale: string
  }
}
export const App: FC<object> = () => {
  const { loading, theme, themeString, teamsUserCredential } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint as string,
    clientId: config.clientId as string
  })

  const fetchContext = async (): Promise<ContextResponse | null> => {
    try {
      const context = await app.getContext()
      return context
    } catch (err) {
      console.log(err)
      return null
    }
  }

  useEffect(() => {
    fetchContext()
      .then(async context => {
        if (context != null) {
          await changeLanguage(context.app.locale)
        }
      })
      .catch(err => console.log(err))
  }, [])

  return (
    <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
      <FluentProvider
        theme={
          themeString === 'dark'
            ? teamsDarkTheme
            : themeString === 'contrast'
              ? teamsHighContrastTheme
              : {
                  ...teamsLightTheme,
                  colorNeutralBackground3: '#ffffff'
                }
        }
        style={{ background: tokens.colorNeutralBackground3 }}
        id='app-container'
      >
        <Router>
          {loading
            ? (
              <Spinner style={{ margin: 100 }} />
              )
            : (
              <Routes>
                <Route path='/privacy' element={<Privacy />} />
                <Route path='/termsofuse' element={<TermsOfUse />} />
                <Route path='/tab' element={<Tab />} />
                <Route path='/configure-tab' element={<ConfigurableTab />} />
                <Route path='/display-tab' element={<DisplayTab />} />
                <Route path='/auth-start' element={<AuthStart />} />
                <Route path='/authCallback' element={<AuthCallback />} />
                <Route path='*' element={<Navigate to='/tab' />} />
              </Routes>
              )}
        </Router>
      </FluentProvider>
    </TeamsFxContext.Provider>
  )
}

export default App
